import {
  FILTERED,
  FluidPaginationState,
  FluidRowData,
  ID,
} from '@lmig/fluid-core';

import { VISIBLE } from '../../../constants/constants';
import { Utils } from '../../../utils/utils';
import { FluidTable } from '../fluid-table';
import {
  setGroupedDataSource,
  updateDataSourceFilterMeta,
} from './filter/fluid-table-filter.utils';
/**
 * Applies filters to the datasource before calculating pagination state and applying to the table
 * @param host
 */
export const applyPagination = (host: Partial<FluidTable>) => {
  updateDataSourceFilterMeta(host);
  if (host.paginated) {
    paginate(host);
  }
};

/**
 * Does the actual pagination of the table
 * @param host
 */
export const paginate = (host) => {
  const start = performance.now();
  if (!host.paginated) return;
  const paginationRange = getPaginationRange(host);
  const { rangeFrom, rangeTo, shouldDecreasePage } = paginationRange;
  if (shouldDecreasePage) {
    host._paginator?.goToPreviousPage().then();
  } else {
    const _start = performance.now();
    const visibleByPage = getVisibleItemsByPage(
      host._dataSource,
      rangeTo,
      rangeFrom
    );

    Utils.logPerformance('getVisibleItemsByPage', _start);

    const __start = performance.now();
    host._dataSource = host._dataSource.map((ds) => ({
      ...ds,
      [VISIBLE]: visibleByPage.find((d) => d[ID] === ds[ID])?.[VISIBLE],
    }));
    Utils.logPerformance('updateDataSource', __start);
    Utils.logPerformance('paginate', start);

    setGroupedDataSource(host);
  }
};

/**
 * Returns true if the given dataItem should be shown in the current table page
 * @param dataItem
 */
export function shouldRenderRow(dataItem): boolean {
  return dataItem[VISIBLE];
}

/**
 * Works out which dataItems are in range, taking into account any items that are
 * already filtered out, so that we can map their visibility to the dataSource upon
 * pagination.
 * @param dataSource
 * @param rangeTo
 * @param rangeFrom
 */
function getVisibleItemsByPage(
  dataSource: FluidRowData[],
  rangeTo: number,
  rangeFrom: number
): FluidRowData[] {
  return dataSource
    .filter((ds) => !ds[FILTERED])
    .filter((dataItem: FluidRowData, idx: number) => {
      if (idx >= rangeFrom && idx <= rangeTo) {
        if (!dataItem[VISIBLE]) {
          rangeTo++;
        } else {
          return dataItem;
        }
      }
    });
}

/**
 * Get the pagination range and check if we should go back to previous page to account for
 * data items changing as a result of deletion.
 * @param host
 */
export const getPaginationRange = (
  host
): { shouldDecreasePage: boolean; rangeTo: number; rangeFrom: number } => {
  const start = performance.now();
  const { paginationState, _dataSource, paginationItemsPerPageOptions } = host;
  const numberOfDataItems = _dataSource.filter(
    (dataItem) => !dataItem[FILTERED]
  ).length;
  const shouldDecreasePage = paginationState?.rangeFrom > numberOfDataItems;
  Utils.logPerformance('getPaginationRange', start);
  return {
    shouldDecreasePage,
    rangeFrom: paginationState ? paginationState.rangeFrom - 1 : 0,
    rangeTo: paginationState
      ? paginationState.rangeTo - 1
      : paginationItemsPerPageOptions[0] - 1,
  };
};

/**
 * Sets the given paginationState on the host
 * @param host
 * @param paginationState
 */
export const setPaginationState = (
  host,
  paginationState: FluidPaginationState
) => {
  host.paginationState = paginationState;
  host.tableStateStore?.setPaginationState(paginationState);
  if (!host.tableStateStore) {
    Utils.consoleWarn(
      { tagName: 'fluid-table' } as HTMLElement,
      'Table store not initialised;'
    );
  }
};
